import { Client, type IMessage } from '@stomp/stompjs';
import type { LiveAlert, LivePosition, StatusCounts } from './types';

export type LiveState = 'connecting' | 'live' | 'reconnecting' | 'off';

export interface LiveHandlers {
  onPosition?: (position: LivePosition) => void;
  onCounts?: (counts: StatusCounts) => void;
  onAlert?: (alert: LiveAlert) => void;
  onState?: (state: LiveState) => void;
  /** The broker refused the token (none, expired, or the account or company is no longer active). */
  onUnauthorized?: () => void;
}

export interface Live {
  start(): void;
  stop(): Promise<void>;
}

/** ws(s)://<api host>/ws/tracking — same origin when baseUrl is ''. */
export function liveUrl(baseUrl: string, location: { protocol: string; host: string }): string {
  if (!baseUrl) return `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/ws/tracking`;
  return `${baseUrl.replace(/^http/, 'ws').replace(/\/$/, '')}/ws/tracking`;
}

/**
 * The legacy API's live feed: STOMP 1.2 on /ws/tracking (the simple broker negotiates no
 * heart-beats; the server pings at WebSocket level). Topics: /topic/tracking (a vehicle's new
 * position), /topic/tracking/counts (the moving/stopped/offline tally), /topic/alerts — only the
 * signed-in user's company's (docs/SAAS.md §3). Every CONNECT carries `token()`, the CURRENT
 * access token, so a reconnect after a refresh signs in with the new one. Reconnects by itself;
 * re-subscribes on every connect.
 */
export function createLive(url: string, handlers: LiveHandlers, token: () => string | null = () => null): Live {
  const json = <T>(handler?: (value: T) => void) => (message: IMessage) => {
    if (!handler) return;
    try {
      handler(JSON.parse(message.body) as T);
    } catch {
      /* a malformed push is dropped, not fatal */
    }
  };

  const client = new Client({
    brokerURL: url,
    reconnectDelay: 3000,
    heartbeatIncoming: 0,
    heartbeatOutgoing: 0,
    debug: () => {},
    beforeConnect: (c) => {
      const current = token();
      c.connectHeaders = current ? { Authorization: `Bearer ${current}` } : {};
    },
  });
  client.onConnect = () => {
    handlers.onState?.('live');
    client.subscribe('/topic/tracking', json(handlers.onPosition));
    client.subscribe('/topic/tracking/counts', json(handlers.onCounts));
    client.subscribe('/topic/alerts', json(handlers.onAlert));
  };
  client.onStompError = (frame) => {
    const reason = frame.headers.message ?? '';
    if (reason === 'Unauthorized' || reason === 'Token expired') handlers.onUnauthorized?.();
  };
  client.onWebSocketClose = () => {
    if (client.active) handlers.onState?.('reconnecting');
  };

  return {
    start() {
      handlers.onState?.('connecting');
      client.activate();
    },
    async stop() {
      await client.deactivate();
      handlers.onState?.('off');
    },
  };
}
